import { fbm, ridge, clamp, hash01, smoothstep, lerp } from '@/core/rng';
import { BIOMES, type BiomeKey, type Site, type WorldParams } from './types';

// ---------------------------------------------------------------------------
// The island's shape, as named quantities. Every length here is in half-map
// units — one unit is half the region's extent, so 1.0 is 25 km on a 50 km
// world — and every height is in normalised elevation, where 1.0 is one
// `heightScale`. Keeping both dimensionless is what lets the same numbers
// describe a 50 km island and a 190 km one.
// ---------------------------------------------------------------------------

/**
 * `fbm` on unit-interval value noise regresses toward 0.5 as its octaves pile
 * up: three octaves span roughly 0.28 to 0.84 with a standard deviation of
 * 0.12, not the 0 to 1 the name suggests. Anything that wants a *swing* — a
 * coast radius, a warp offset — must be stretched back out first, or the
 * amplitude it gets multiplied by is quietly a quarter of what it says it is.
 * This is why the coastline stayed a circle through two rounds of raising the
 * numbers that were supposed to break it.
 */
const swing = (v: number) => clamp((v - 0.5) * 3.2, -1, 1);

/** Mean distance from the centre to the waterline. */
const COAST_R = 0.665;
/**
 * How far the coast warp bends the circle. Its *frequency* matters as much as
 * its size: warping at a wavelength as long as the island only slides the whole
 * landmass sideways, which changes nothing. It has to vary across the island to
 * deform it.
 */
const COAST_WARP = 0.16;
/** Headland/bay swing, then coves, then the fret of the shore itself. */
const COAST_LOBE = 0.240, COAST_COVE = 0.055, COAST_FRET = 0.016;

/** Offshore rocks: how many are tried, how wide they are, how tall they stand. */
const ISLETS = 10;
const ISLET_R0 = 0.020, ISLET_R1 = 0.070, ISLET_H = 0.062;

/**
 * Where high country begins in the mass field, and how sharp its flank is.
 * The threshold sits high on purpose: three octaves of fbm cluster around their
 * own mean, so a midpoint anywhere near it promotes most of the island to
 * mountain and the result is one dome. Only the top sixth or so may be high
 * country — the rest has to be plains, foothills and passes for the high
 * country to read as high at all.
 */
const MASS_MID = 0.655, MASS_EDGE = 0.062;
/** Height the massifs reach above the low country. */
const MASSIF = 0.85;
/** How far the relief warp drags the ranges sideways. */
const RELIEF_WARP = 0.62;
/** Benches per unit of elevation: 15 over a 2.7 km island is a ~180 m tread. */
const TERRACE_STEPS = 15;

/** Height of the coastal step, on a soft shore and on a hard one. */
const BROW_SOFT = 0.005, BROW_HARD = 0.050;
/** Distance over which that step is climbed — 2.9 km of beach, or 340 m of cliff. */
const RAMP_SOFT = 0.115, RAMP_HARD = 0.0135;
/** Distance over which the interior relief comes in behind it. */
const INLAND_SOFT = 0.170, INLAND_HARD = 0.058;

/** Depth of the shelf break, and of the basin beyond it. */
const SHELF_D = 0.024, ABYSS = 0.170;
/** Width of the shelf under a beach coast, and under a cliff coast. */
const SHELF_SOFT = 0.125, SHELF_HARD = 0.022;
/** The seabed may shoal, but the waterline belongs to the land. */
const SEABED_MIN = 4e-4;

/** Accumulated flow at which a cell owns a valley floor. */
const CHANNEL = 200;
/**
 * How far above its channel a floor may still be pulled flat. It has to be of
 * the same order as the hills the shape puts in the low country, or the
 * floodplains it is supposed to cut get buried under them.
 */
const FLOOR_REACH = 0.030;
/** How much a floodplain rises per cell away from its channel. */
const FLOOR_RISE = 1.1e-3;

/** Gradients (m/m) between which hillslope creep gives up on smoothing. */
const CREEP0 = 0.16, CREEP1 = 0.68;

/**
 * Steps in a smooth field: a wide tread, then a fast riser.
 *
 * Rock is layered and layers weather at different rates, which is why a real
 * mountainside gives back benches rather than one clean ramp. It pays twice
 * here: the risers are also the only thing that makes the ground steep enough
 * for `chunk.ts`'s rock ramp and `render/rocks.ts`'s scree to ever fire.
 *
 * Continuous at every step boundary (both ends of the smoothstep are flat), so
 * terracing never introduces a cliff the erosion passes would have to repair.
 */
const terrace = (h: number, steps: number): number => {
  const s = h * steps, i = Math.floor(s);
  return (i + smoothstep(0.46, 1, s - i)) / steps;
};

/** Catmull-Rom through four samples; `t` runs 0..1 between the middle two. */
const cubic = (a: number, b: number, c: number, d: number, t: number): number => {
  const t2 = t * t;
  return b + 0.5 * t * (c - a)
           + t2 * (a - 2.5 * b + 2 * c - 0.5 * d)
           + t2 * t * (1.5 * b - 0.5 * a - 1.5 * c + 0.5 * d);
};

/** A built Region as plain transferable data — everything but the methods. */
export interface RegionSnapshot {
  params: WorldParams;
  elev: Float32Array; flow: Float32Array; rain: Float32Array; temp: Float32Array;
  ocean: Uint8Array; biome: Uint8Array;
  down: Int32Array;
  seaLevel: number; maxHeight: number;
}

/**
 * The region: one coarse grid holding elevation, drainage, climate and habitat.
 *
 * This is the authoritative coarse state. Chunks refine it; they never contradict
 * it. Built once, then only read — so it is safe to share with workers.
 */
export class Region {
  readonly n: number;
  readonly cell: number;
  readonly heightScale: number;
  readonly seed: number;
  readonly island: boolean;

  readonly elev: Float32Array;
  readonly flow: Float32Array;
  readonly rain: Float32Array;
  readonly temp: Float32Array;
  readonly ocean: Uint8Array;
  readonly biome: Uint8Array;
  /**
   * Where each cell's water goes: the D8 downstream index from the LAST
   * routing pass, on the priority-flood-filled field — so it runs *through*
   * flats and never strands in a bicubic pit. −1 below the sea. `flow` was
   * always accumulated along exactly these edges; keeping them is what lets
   * the drawn rivers follow the same water the simulation counted.
   */
  down: Int32Array = new Int32Array(0);
  seaLevel = 0;
  maxHeight = 1;

  // public because `world/landmarks.ts` reads `biome` directly off the grid to
  // find isolated groves, and needs the same index → key mapping this uses
  readonly biomeKeys: BiomeKey[] = Object.keys(BIOMES) as BiomeKey[];

  constructor(p: WorldParams, snap?: RegionSnapshot) {
    this.n = p.n;
    this.cell = p.cellSize;
    this.heightScale = p.heightScale;
    this.seed = p.seed;
    this.island = p.island ?? false;
    if (snap) {
      // adopt already-built state — byte-identical to building, and instant
      this.elev = snap.elev; this.flow = snap.flow;
      this.rain = snap.rain; this.temp = snap.temp;
      this.ocean = snap.ocean; this.biome = snap.biome;
      this.down = snap.down;
      this.seaLevel = snap.seaLevel; this.maxHeight = snap.maxHeight;
      return;
    }
    const size = p.n * p.n;
    this.elev = new Float32Array(size);
    this.flow = new Float32Array(size);
    this.rain = new Float32Array(size);
    this.temp = new Float32Array(size);
    this.ocean = new Uint8Array(size);
    this.biome = new Uint8Array(size);
    this.build();
  }

  /**
   * The built state as plain data, safe to structured-clone to a worker.
   * Methods do not survive the wire; the arrays do, byte for byte — so a
   * worker can wrap a Region around them without redoing the build.
   */
  toSnapshot(): RegionSnapshot {
    return {
      params: { seed: this.seed, n: this.n, cellSize: this.cell,
                heightScale: this.heightScale, island: this.island },
      elev: this.elev, flow: this.flow, rain: this.rain, temp: this.temp,
      ocean: this.ocean, biome: this.biome,
      down: this.down,
      seaLevel: this.seaLevel, maxHeight: this.maxHeight,
    };
  }

  static fromSnapshot(s: RegionSnapshot): Region {
    return new Region(s.params, s);
  }

  private idx(x: number, y: number) { return y * this.n + x; }

  /**
   * Accumulated flow at a world position, bilinearly interpolated — where
   * `siteAt` snaps to the nearest cell. The nearest-cell read is right for
   * classification and wrong for *shaping*: anything that moulds the drawn
   * ground by flow needs a value that varies continuously, or the moulding
   * arrives in 260 m tiles.
   */
  flowAt(wx: number, wz: number): number {
    const fx = wx / this.cell + this.n / 2 - 0.5;
    const fz = wz / this.cell + this.n / 2 - 0.5;
    const x0 = Math.max(0, Math.min(this.n - 2, Math.floor(fx)));
    const z0 = Math.max(0, Math.min(this.n - 2, Math.floor(fz)));
    const u = Math.min(1, Math.max(0, fx - x0)), v = Math.min(1, Math.max(0, fz - z0));
    const a = this.flow[this.idx(x0, z0)], b = this.flow[this.idx(x0 + 1, z0)];
    const c = this.flow[this.idx(x0, z0 + 1)], d = this.flow[this.idx(x0 + 1, z0 + 1)];
    return (a + (b - a) * u) * (1 - v) + (c + (d - c) * u) * v;
  }

  private build() {
    this.shape();
    // carve, then let the rivers lay their own floors back down in the last
    // rounds — deposition needs a drainage network to deposit along
    for (let i = 0; i < 7; i++) {
      this.route();
      this.incise();
      if (i >= 3) this.deposit();
    }
    // the LAST routing is the network the shipped flow field was counted
    // along — kept, so the drawn rivers can follow the same water
    this.down = this.route();
    this.floodOcean();
    this.climate();
    this.classify();
  }

  private shape() {
    if (this.island) this.shapeIsland();
    else this.shapeContinent();
  }

  /**
   * The island: a warped coast laid over separate massifs.
   *
   * Two fields do all the work, and they are deliberately independent of one
   * another.
   *
   * `land` is a *signed shore field* — positive inland, negative at sea, zero
   * on the waterline. It began as a circle and is one only in that sense: the
   * position its radius is measured from is domain-warped, and the radius
   * itself is then swung by a lobe field and fretted by two finer ones, so what
   * comes out has bays, headlands, peninsulas and coves instead of a fringe.
   * Because the waterline is exactly this field's zero contour, sea level is
   * exactly zero — which is what lets the shore profile and the submarine
   * profile be *designed* rather than discovered from a histogram.
   *
   * `relief` is the land's own shape and knows nothing about the coast. A
   * low-frequency mass field decides where high country is allowed to be at
   * all — two or three separate blocks with saddles between them, not one dome
   * — and domain-warped ridged noise draws the crests and spurs inside them.
   * The *edge* of that mass field is the mountain flank, so its width is
   * literally how steep the mountains are. Terracing then puts treads and
   * risers into the upper profile, so a hillside has steps to stand on.
   *
   * The two meet at the shore, and the rate of the rise there decides the
   * coast's character: a headland comes up as a cliff within a cell or two and
   * the shelf falls away beneath it; a bay head comes up over kilometres as a
   * beach and shoals out for as far again.
   */
  private shapeIsland() {
    const { n, seed } = this;
    this.seaLevel = 0;

    // The offshore rocks, decided once: a hashed ring of small satellites
    // sitting just beyond the mean shore. `floodOcean` calls ocean whatever
    // reaches the map edge, so every one of these that clears the coast stands
    // as an island in its own right, and any that does not becomes a headland.
    const islets = Array.from({ length: ISLETS }, (_, k) => {
      const a = hash01(k, 0, seed + 701) * Math.PI * 2;
      const d = COAST_R + 0.03 + hash01(k, 1, seed + 703) * 0.31;
      return {
        x: Math.cos(a) * d,
        y: Math.sin(a) * d,
        r: ISLET_R0 + hash01(k, 2, seed + 707) * (ISLET_R1 - ISLET_R0),
        tall: 0.4 + hash01(k, 3, seed + 709) * 1.2,
      };
    });

    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      // one unit is half the map, so p and q run -1..1 about the centre
      const p = (x / n - 0.5) * 2, q = (y / n - 0.5) * 2;

      // ---- the coast ----------------------------------------------------
      // Bend the position the radius is measured from. This one line is the
      // difference between a disc with a ragged fringe and a landmass with
      // limbs: warping the *domain* moves whole kilometres of shore at a time.
      const bx = swing(fbm(p * 2.20 + 3.7, q * 2.20 + 1.9, 3, seed + 31));
      const by = swing(fbm(p * 2.20 + 8.3, q * 2.20 + 6.1, 3, seed + 37));
      const rw = Math.hypot(p + bx * COAST_WARP, q + by * COAST_WARP);
      // then swing the radius itself: lobes are headlands, notches are bays
      const lobe = swing(fbm(p * 2.45 + 11.3, q * 2.45 + 17.1, 4, seed + 41));
      const cove = swing(fbm(p * 6.30 + 23.7, q * 6.30 + 29.3, 3, seed + 43));
      const fret = swing(fbm(p * 15.5 + 31.1, q * 15.5 + 37.9, 3, seed + 47));
      let land = COAST_R + lobe * COAST_LOBE + cove * COAST_COVE + fret * COAST_FRET - rw;

      let sat = -9, satR = 1, satTall = 0;
      for (const it of islets) {
        const s = it.r - Math.hypot(p - it.x, q - it.y);
        if (s > sat) { sat = s; satR = it.r; satTall = it.tall; }
      }
      const onSat = sat > land;
      if (onSat) land = sat;
      // no land may reach the edge of the world: the sea has to get out, or
      // `floodOcean` cannot tell the open ocean from a lake. After the union,
      // so a satellite cannot smuggle itself past the guard.
      land -= smoothstep(0.74, 0.96, Math.max(Math.abs(p), Math.abs(q))) * 1.3;

      // How hard the rock is where the sea meets it. A headland is the stone
      // the water could not take, so it stands as a cliff; a bay is where the
      // soft ground went, so it lies down as a beach. One field, so a coast's
      // plan and its profile always agree with each other.
      const hard = smoothstep(-0.10, 0.42, lobe + cove * 0.4);

      let e: number;
      if (land > 0) {
        // ---- the land's own shape ---------------------------------------
        // one warp shared by the whole relief stack, so ranges bend together
        // instead of each octave wandering off on its own
        const ax = fbm(p * 0.85 + 2.3, q * 0.85 + 4.7, 4, seed + 53) - 0.5;
        const ay = fbm(p * 0.85 + 7.9, q * 0.85 + 9.4, 4, seed + 59) - 0.5;
        const rx = p + ax * RELIEF_WARP, ry = q + ay * RELIEF_WARP;

        // where high country is allowed at all — and the width of this edge is
        // the steepness of every mountain flank on the island
        const massN = fbm(rx * 1.30 + 13.1, ry * 1.30 + 19.7, 3, seed + 67);
        const w = MASS_EDGE + (fbm(rx * 3.7 + 61.3, ry * 3.7 + 67.9, 2, seed + 71) - 0.5) * 0.076;
        const mass = smoothstep(MASS_MID - w, MASS_MID + w, massN);
        // The crests inside them, as four bands rather than one.
        //
        // `ridge`'s octaves halve as they double in frequency, so a single call
        // is dominated by its own longest wavelength: ask it for a mountain
        // range across a 50 km island and it returns a swell 25 km wide with
        // the top in the middle, however many octaves it is given. That is a
        // dome, and no amount of height fixes it.
        //
        // What a mountainside is actually read at is two to six kilometres —
        // near enough that a valley is a valley and a flank is a flank — so
        // most of the amplitude belongs in bands whose *base* frequency is
        // already there. The long band below only orients the island; the
        // middle two carry the ranges and the individual mountains between
        // them, and the last is ravine texture.
        const spine = ridge(rx * 1.95 + 31.3, ry * 1.95 + 37.1, 5, seed + 73);
        const arete = ridge(rx * 5.60 + 41.7, ry * 5.60 + 47.3, 4, seed + 79);
        const crag = ridge(rx * 13.0 + 83.1, ry * 13.0 + 89.7, 3, seed + 97);
        const spur = ridge(rx * 21.0 + 67.1, ry * 21.0 + 71.9, 3, seed + 79);

        // The low country first: coastal plain, and foothills with a scale of
        // their own. Without the second term the whole seaward half of the
        // island is one green swell sixteen kilometres across — the same fault
        // as the dome, just lower down and easier to forgive.
        let relief = 0.035 + fbm(rx * 3.05 + 53.1, ry * 3.05 + 59.7, 5, seed + 83) * 0.135;
        const hill = ridge(rx * 9.5 + 113.1, ry * 9.5 + 119.7, 3, seed + 127);
        relief += (hill - 0.34) * 0.085 * (1 - mass * 0.7);
        // then the massifs standing on it
        relief += mass * (0.16 + 0.26 * Math.pow(spine, 1.25)
                               + 0.52 * Math.pow(arete, 1.4)) * MASSIF;
        // and the mountains and ravines inside those, gated on the high country
        // so the plains and the valley floors stay smooth
        relief += (crag - 0.30) * 0.30 * mass;
        relief += (spur - 0.34) * 0.15 * smoothstep(0.12, 0.50, relief);
        // and the grain of the rock
        relief += (fbm(rx * 10.5 + 71.3, ry * 10.5 + 79.1, 4, seed + 89) - 0.5) * 0.05;

        // Benches. A slope that rises at one rate for two kilometres reads as
        // upholstery whatever height you give it. The phase drifts across the
        // island so these are benches on a mountain rather than contour lines
        // on a map, and the strength fades out low down, where soil and rivers
        // would long since have smoothed them away.
        const phase = (fbm(rx * 2.15 + 91.3, ry * 2.15 + 97.7, 3, seed + 101) - 0.5) * 0.08;
        const amt = smoothstep(0.15, 0.44, relief)
                  * (0.24 + 0.52 * fbm(rx * 4.3 + 103.1, ry * 4.3 + 107.9, 3, seed + 109));
        relief = Math.max(0.010, lerp(relief, terrace(relief + phase, TERRACE_STEPS) - phase, amt));

        // ---- where the two fields meet ----------------------------------
        const brow = lerp(BROW_SOFT, BROW_HARD, hard);
        const step = smoothstep(0, lerp(RAMP_SOFT, RAMP_HARD, hard), land);
        const rise = smoothstep(0, lerp(INLAND_SOFT, INLAND_HARD, hard), land);
        e = brow * step + relief * rise;
        // a sea stack is a stump of the same rock: a small footprint standing
        // taller than that footprint would suggest
        if (onSat) e += satTall * ISLET_H * Math.pow(smoothstep(0, satR * 0.9, sat), 0.55);
      } else {
        // ---- the sea floor ----------------------------------------------
        // the shelf the water shader reads for its shallows and its foam, then
        // the fall into the basin; banks and channels so it is not a cone
        const d = -land;
        const shelfW = lerp(SHELF_SOFT, SHELF_HARD, hard);
        e = -(SHELF_D * smoothstep(0, shelfW, d) + ABYSS * smoothstep(shelfW * 0.7, 0.60, d))
          + (fbm(p * 9.5 + 51.3, q * 9.5 + 57.7, 3, seed + 61) - 0.5)
            * 0.009 * smoothstep(0.003, 0.05, d);
        if (e > -SEABED_MIN) e = -SEABED_MIN;
      }
      this.elev[this.idx(x, y)] = e;
    }
  }

  /** A continental interior: broad landmass, chains, and a shelf to the west. */
  private shapeContinent() {
    const { n, seed } = this;
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      const u = x / n, v = y / n;
      const wx = u * 3.1 + fbm(u * 2.2, v * 2.2, 4, seed + 9) * 0.55;
      const wy = v * 3.1 + fbm(u * 2.2 + 5, v * 2.2 + 5, 4, seed + 11) * 0.55;
      const cont = fbm(wx * 0.9, wy * 0.9, 5, seed) * 1.25 - 0.18;
      const chain = ridge(wx * 1.7, wy * 1.7, 6, seed + 3);
      let e = cont * 0.62 + chain * chain * 0.52 * smoothstep(0.24, 0.62, cont)
            + fbm(wx * 4.5, wy * 4.5, 6, seed + 5) * 0.14;
      e -= smoothstep(0.14, 0, u) * 0.12;
      const rim = Math.max(Math.abs(u - 0.5), Math.abs(v - 0.5)) * 2;
      e -= smoothstep(0.74, 1.0, rim) * 0.55;
      this.elev[this.idx(x, y)] = e;
    }
    const sample: number[] = [];
    for (let i = 0; i < this.elev.length; i += 7) sample.push(this.elev[i]);
    sample.sort((a, b) => a - b);
    this.seaLevel = sample[(sample.length * 0.34) | 0];   // a continent is mostly land
  }

  /** Priority-flood depression filling, then steepest descent and accumulation. */
  private route(): Int32Array {
    const { n } = this;
    const filled = Float32Array.from(this.elev);
    const inQ = new Uint8Array(n * n);
    const heap: number[] = [];
    const push = (i: number) => {
      heap.push(i);
      let c = heap.length - 1;
      while (c > 0) {
        const p = (c - 1) >> 1;
        if (filled[heap[p]] <= filled[heap[c]]) break;
        [heap[p], heap[c]] = [heap[c], heap[p]];
        c = p;
      }
    };
    const pop = () => {
      const top = heap[0], last = heap.pop()!;
      if (heap.length) {
        heap[0] = last;
        let c = 0;
        for (;;) {
          const l = c * 2 + 1, r = l + 1;
          let m = c;
          if (l < heap.length && filled[heap[l]] < filled[heap[m]]) m = l;
          if (r < heap.length && filled[heap[r]] < filled[heap[m]]) m = r;
          if (m === c) break;
          [heap[m], heap[c]] = [heap[c], heap[m]];
          c = m;
        }
      }
      return top;
    };
    for (let x = 0; x < n; x++) for (const i of [this.idx(x, 0), this.idx(x, n - 1)])
      if (!inQ[i]) { inQ[i] = 1; push(i); }
    for (let y = 0; y < n; y++) for (const i of [this.idx(0, y), this.idx(n - 1, y)])
      if (!inQ[i]) { inQ[i] = 1; push(i); }
    for (let i = 0; i < n * n; i++)
      if (this.elev[i] <= this.seaLevel && !inQ[i]) { inQ[i] = 1; push(i); }

    const EPS = 2.4e-5;
    while (heap.length) {
      const i = pop(), x = i % n, y = (i / n) | 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue;
        const j = this.idx(nx, ny);
        if (inQ[j]) continue;
        // an irregular ramp across flats, or rivers draw in right angles
        if (filled[j] <= filled[i]) filled[j] = filled[i] + EPS * (0.35 + ((nx * 7 + ny * 13) % 19) / 10);
        inQ[j] = 1;
        push(j);
      }
    }

    const down = new Int32Array(n * n).fill(-1);
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      const i = this.idx(x, y);
      if (this.elev[i] <= this.seaLevel) continue;
      let best = -1, bd = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue;
        const j = this.idx(nx, ny);
        const d = (filled[i] - filled[j]) / (dx && dy ? 1.414 : 1);
        if (d > bd) { bd = d; best = j; }
      }
      down[i] = best;
    }
    const order = Array.from({ length: n * n }, (_, i) => i).sort((a, b) => filled[b] - filled[a]);
    this.flow.fill(1);
    for (const i of order) { const d = down[i]; if (d >= 0) this.flow[d] += this.flow[i]; }
    return down;
  }

  /** Rivers cut down; slopes relax. This is what makes valleys look carved. */
  private incise() {
    const { n } = this;
    const g2s = this.heightScale / this.cell;      // normalised gradient → m/m
    // Stream power: a channel cuts in proportion to the water it carries *and*
    // to how steeply it is already falling. The slope term is what turns a
    // smooth upland into ravines and aretes — with discharge alone, only the
    // trunk valleys ever deepen and the mountains keep whatever shape the noise
    // handed them. It also spares the flat trunk floors, which is where the
    // river should be laying sediment down rather than cutting.
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      const i = this.idx(x, y);
      if (this.elev[i] <= this.seaLevel) continue;
      const gx = x > 0 && x < n - 1 ? (this.elev[i + 1] - this.elev[i - 1]) * 0.5 : 0;
      const gy = y > 0 && y < n - 1 ? (this.elev[i + n] - this.elev[i - n]) * 0.5 : 0;
      const grade = Math.hypot(gx, gy) * g2s;
      this.elev[i] -= Math.min(0.055, Math.pow(this.flow[i], 0.42) * 2.6e-4
                                      * (0.45 + 1.75 * smoothstep(0.06, 0.75, grade)));
    }
    // Hillslope creep, and only creep. Soil moves downhill and rounds gentle
    // ground off, but a rock face does not average itself away — so this is
    // strongest where the land is already gentle and nearly absent on a wall.
    // Unweighted, seven relaxation passes quietly erase every steep face the
    // shape built, which is most of why the old island read as upholstery.
    // Reading from a snapshot keeps it isotropic: in place, the sweep smears
    // the field in the direction it happens to travel.
    const prev = Float32Array.from(this.elev);
    for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) {
      const i = this.idx(x, y);
      if (prev[i] <= this.seaLevel) continue;
      const avg = (prev[i - 1] + prev[i + 1] + prev[i - n] + prev[i + n]) * 0.25;
      const grade = Math.hypot(prev[i + 1] - prev[i - 1], prev[i + n] - prev[i - n]) * 0.5 * g2s;
      // a wall keeps most of its angle, but not all of it: the small residue
      // is what stops a coincidence of noise standing up as a vertical sheet
      this.elev[i] += (avg - prev[i]) * 0.13 * (0.12 + 0.88 * (1 - smoothstep(CREEP0, CREEP1, grade)));
    }
  }

  /**
   * Alluvium: the flat floors that make a valley a place rather than a crease.
   *
   * Incision on its own cuts a V and keeps cutting, so every valley bottoms out
   * in a line. A real river does not: it swings across its own valley, widens
   * the floor and backfills it with what it carried, and the result is a flat
   * strip of ground between two steep walls. That flat strip is where people
   * walk, camp and settle, so it matters more than anything else the erosion
   * does here.
   *
   * Every cell that carries water claims a floor whose width grows with its
   * flow; each cell in reach is pulled down toward that floor, but only in
   * proportion to how close it already is — so the bottom of the V flattens
   * out while the hillside above it is untouched, and the walls get *steeper*
   * for the trouble. Runs between the routing passes, so the network it follows
   * is the network the finished map has.
   */
  private deposit() {
    const { n } = this;
    const floor = new Float32Array(n * n).fill(Infinity);
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      const i = this.idx(x, y);
      const f = this.flow[i];
      if (f < CHANNEL || this.elev[i] <= this.seaLevel) continue;
      const h = this.elev[i];
      // a bigger river owns a wider floor: one cell per doubling, and change
      const rad = Math.min(3, 1 + Math.floor(Math.log(f / CHANNEL) * 0.85));
      for (let dy = -rad; dy <= rad; dy++) for (let dx = -rad; dx <= rad; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue;
        const d = Math.hypot(dx, dy);
        if (d > rad) continue;
        // the floodplain lifts a little away from the channel — a valley
        // bottom, not a canal — and the lowest claim wins
        const t = h + d * FLOOR_RISE;
        if (t < floor[this.idx(nx, ny)]) floor[this.idx(nx, ny)] = t;
      }
    }
    for (let i = 0; i < n * n; i++) {
      const f = floor[i];
      if (!isFinite(f) || this.elev[i] <= this.seaLevel) continue;
      const above = this.elev[i] - f;
      if (above <= 0) continue;
      this.elev[i] -= above * (1 - smoothstep(0, FLOOR_REACH, above)) * 0.8;
    }
  }

  /** Ocean is what reaches the edge of the world. Inland hollows become lakes. */
  private floodOcean() {
    const { n } = this;
    this.ocean.fill(0);
    const q: number[] = [];
    const add = (i: number) => {
      if (!this.ocean[i] && this.elev[i] <= this.seaLevel) { this.ocean[i] = 1; q.push(i); }
    };
    for (let x = 0; x < n; x++) { add(this.idx(x, 0)); add(this.idx(x, n - 1)); }
    for (let y = 0; y < n; y++) { add(this.idx(0, y)); add(this.idx(n - 1, y)); }
    for (let h = 0; h < q.length; h++) {
      const i = q[h], x = i % n, y = (i / n) | 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue;
        add(this.idx(nx, ny));
      }
    }
    for (let i = 0; i < n * n; i++)
      if (!this.ocean[i] && this.elev[i] <= this.seaLevel) this.elev[i] = this.seaLevel + 0.0008;
  }

  /**
   * Orographic rain from a westerly, then temperature by latitude and altitude.
   *
   * Every rate here is *per cell*, and the constants were fitted on a 972 m
   * grid — so on the island's 260 m grid the air crossed the same mountain in
   * four times as many steps and shed its moisture four times as fast per
   * kilometre. One climb exhausted it, the whole lee went to desert, and the
   * montane forest band never existed. `cellScale` converts the rates back into
   * distance, so the same model describes both grids; at 972 m it is exactly 1
   * and the continent is unchanged to the bit.
   */
  private climate() {
    const { n } = this;
    const cellScale = this.cell / 972;
    for (let y = 0; y < n; y++) {
      let moist = 1.0;
      for (let x = 0; x < n; x++) {
        const i = this.idx(x, y);
        if (this.ocean[i]) {
          moist = Math.min(1.25, moist + 0.09 * cellScale);
          this.rain[i] = 0.62;
          continue;
        }
        const e = Math.max(0, this.elev[i] - this.seaLevel);
        const prev = x > 0 ? Math.max(0, this.elev[i - 1] - this.seaLevel) : 0;
        // fraction of the column shed crossing this cell: the ascent per unit
        // of distance is what wrings out the air, not the ascent per cell
        const drop = clamp(Math.max(0, e - prev) * 7.5 / cellScale + 0.008 * cellScale,
                           0, 0.30 * cellScale) * moist;
        moist = Math.max(0.15, moist - drop * 0.58 + 0.0125 * cellScale);
        // and what falls is that shedding per unit of *area*
        this.rain[i] = clamp(drop / cellScale * 1.9 + moist * 0.46, 0, 1);
      }
    }
    const tmp = Float32Array.from(this.rain);
    for (let y = 1; y < n - 1; y++) for (let x = 1; x < n - 1; x++) {
      const i = this.idx(x, y);
      this.rain[i] = tmp[i] * 0.4 + (tmp[i - 1] + tmp[i + 1] + tmp[i - n] + tmp[i + n]) * 0.15;
    }
    for (let i = 0; i < n * n; i++) {
      const y = (i / n) | 0;
      this.temp[i] = clamp(
        1 - Math.abs(y / n - 0.42) * 1.5 - Math.max(0, this.elev[i] - this.seaLevel) * 1.5 - 0.05, 0, 1);
    }
  }

  /**
   * How many cells of land lie between each cell and the open water. Chebyshev
   * distance by one sweep out from the shore — cheap, and enough to tell a
   * coast forest from an inland one. On an island this has to be a real
   * measurement: "coastal" cannot mean "the western third of the map" when
   * every side of the world is sea.
   */
  private shoreDistance(): Float32Array {
    const { n } = this;
    const d = new Float32Array(n * n).fill(Infinity);
    const q: number[] = [];
    for (let i = 0; i < n * n; i++) if (this.ocean[i]) { d[i] = 0; q.push(i); }
    for (let h = 0; h < q.length; h++) {
      const i = q[h], x = i % n, y = (i / n) | 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue;
        const j = this.idx(nx, ny);
        if (d[j] !== Infinity) continue;
        d[j] = d[i] + 1;
        q.push(j);
      }
    }
    return d;
  }

  /** Habitat from height, rain and drainage — normalised to this region's own range. */
  private classify() {
    const { n } = this;
    this.maxHeight = 1e-4;
    for (let i = 0; i < n * n; i++) this.maxHeight = Math.max(this.maxHeight, this.elev[i] - this.seaLevel);
    const shore = this.shoreDistance();
    // the coastal band is a distance, not a side of the map: about 3 km in
    const NEAR = Math.max(2, Math.round(3200 / this.cell));
    const g2s = this.heightScale / this.cell;   // normalised gradient → m/m
    for (let i = 0; i < n * n; i++) {
      if (this.ocean[i]) { this.biome[i] = this.biomeKeys.indexOf('ocean'); continue; }
      const h = (this.elev[i] - this.seaLevel) / this.maxHeight;
      const r = this.rain[i], f = this.flow[i];
      const coastal = shore[i] <= NEAR;
      const x = i % n, y = (i / n) | 0;
      let k: BiomeKey;
      if (h < 0.022) {
        // low ground at the water's edge is a shore — unless it is falling into
        // the sea, in which case it is a cliff and nothing takes root on it
        const gx = x > 0 && x < n - 1 ? (this.elev[i + 1] - this.elev[i - 1]) * 0.5 : 0;
        const gy = y > 0 && y < n - 1 ? (this.elev[i + n] - this.elev[i - n]) * 0.5 : 0;
        const cliff = Math.hypot(gx, gy) * g2s > 0.17;
        k = cliff ? 'chaparral' : r > 0.5 ? 'marsh' : 'beach';
      }
      else if (f > 1400) k = 'riparian';
      else if (h > 0.84) k = 'alpine';
      else if (h > 0.58) k = r > 0.40 ? 'conifer' : 'chaparral';
      else if (r > 0.62) k = coastal ? 'redwood' : 'riparian';
      else if (r > 0.42) k = 'oak';
      else if (r > 0.28) k = coastal ? 'scrub' : 'grassland';
      else if (r > 0.16) k = 'chaparral';
      else k = 'desert';
      this.biome[i] = this.biomeKeys.indexOf(k);
    }
  }

  /**
   * Bicubic sample of any field, in world metres.
   *
   * Bilinear was enough while the land was gentle: it is only C0, so there is a
   * crease along every cell boundary, and a crease is invisible on a one-in-ten
   * grade. On a forty-degree flank it is not — the whole mountainside breaks
   * into flat 260 m facets and the eye reads the grid instead of the hill.
   * Catmull-Rom has a continuous tangent across the boundaries, so the facets
   * go without the relief that exposed them having to go with them.
   *
   * Only `heightAt` reads through here; rain, flow and biome are sampled by
   * nearest cell, and the hydrology works on the raw grid — so this changes how
   * the land is *drawn*, never how it drains.
   */
  private sample(arr: Float32Array, wx: number, wz: number): number {
    const n = this.n;
    const fx = clamp(wx / this.cell + n / 2, 0, n - 1.001);
    const fy = clamp(wz / this.cell + n / 2, 0, n - 1.001);
    const i = fx | 0, j = fy | 0, u = fx - i, v = fy - j;
    const x0 = i > 0 ? i - 1 : 0, x2 = i + 1 < n ? i + 1 : n - 1;
    const x3 = i + 2 < n ? i + 2 : n - 1;
    const row = (y: number): number => {
      const o = y * n;
      return cubic(arr[o + x0], arr[o + i], arr[o + x2], arr[o + x3], u);
    };
    const y0 = j > 0 ? j - 1 : 0, y2 = j + 1 < n ? j + 1 : n - 1;
    const y3 = j + 2 < n ? j + 2 : n - 1;
    return cubic(row(y0), row(j), row(y2), row(y3), v);
  }

  private nearest(wx: number, wz: number): number {
    const x = clamp(Math.round(wx / this.cell + this.n / 2), 0, this.n - 1);
    const y = clamp(Math.round(wz / this.cell + this.n / 2), 0, this.n - 1);
    return this.idx(x, y);
  }

  /** Coarse ground height in metres above sea level. */
  heightAt(wx: number, wz: number): number {
    return (this.sample(this.elev, wx, wz) - this.seaLevel) * this.heightScale;
  }

  /** Everything the region knows about a point. */
  siteAt(wx: number, wz: number): Site {
    const i = this.nearest(wx, wz);
    return {
      height: this.heightAt(wx, wz),
      rain: this.rain[i],
      flow: this.flow[i],
      biome: this.biomeKeys[this.biome[i]],
      land: !this.ocean[i],
    };
  }

  get extentMetres(): number { return this.n * this.cell; }
}
